import React, { useRef, useMemo, useState } from "react";
import { Component, Viewport, CardProperties } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import { useInteractionContext } from "@/components/shared/rendering/InteractionContextRuntime";
import { SEAFOAM_GREEN } from "@/styles/tokens";
import { ComponentWrapper } from "@/components/shared/rendering/ComponentWrapper";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { CONTROL_THEME } from "@/components/controls/controlThemeTokens";

interface CardProps {
  component: Component;
  isPreview?: boolean;
  children?: Component[];
  /** Current viewport for responsive behavior */
  viewport?: Viewport;
  activeColorScheme?: ColorScheme;
}

// Card-specific defaults - explicit token-aware background, borders, shadow
// These are the key differentiators from FlexContainer
// Border radius: 8px per style guide (6-8px for cards/modals)
const DEFAULT_CARD_PROPERTIES: CardProperties = {
  // Visual properties (card-specific - NOT transparent like FlexContainer)
  backgroundColor: CONTROL_THEME.card,
  borderWidth: 1,
  borderColor: CONTROL_THEME.border,
  borderStyle: "solid",
  borderRadius: 8, // Style guide: 6-8px for cards/modals
  boxShadow: "0 1px 3px hsl(var(--foreground) / 0.08)",
  padding: 24,

  // Layout properties (simplified from FlexContainer)
  flexDirection: "column",
  gap: 16,
  alignItems: "stretch",
  justifyContent: "flex-start",

  // Metadata
  label: "Card",
};

export const Card: React.FC<CardProps> = ({
  component,
  isPreview = false,
  children = [],
  viewport = "desktop",
  activeColorScheme,
}) => {
  // Get interaction context to determine if we're in viewer mode
  const interactionContext = useInteractionContext();

  // Auto-detect responsive breakpoint based on window size
  // In viewer/functional mode, use detected breakpoint
  // In editing mode, use provided viewport (for multi-viewport editor)
  const detectedBreakpoint = useResponsiveViewport();
  const activeViewport = (
    interactionContext.mode === "editing" ? viewport : detectedBreakpoint
  ) as Viewport;

  // Container DOM reference
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Apply responsive overrides based on active viewport
  const responsiveProperties = useMemo(() => {
    const { responsivePosition } = component;
    const properties: CardProperties = {
      ...DEFAULT_CARD_PROPERTIES,
      ...component.properties,
    };

    // If no responsivePosition, return default properties
    if (!responsivePosition) {
      return properties;
    }

    // Get current breakpoint from activeViewport
    const breakpoint =
      activeViewport === "mobile"
        ? "mobile"
        : activeViewport === "tablet"
          ? "tablet"
          : "desktop";

    // Apply breakpoint overrides if they exist
    const breakpointOverride = responsivePosition.breakpoints?.[breakpoint];
    if (!breakpointOverride?.style) {
      return properties;
    }

    // Merge with breakpoint-specific styles
    return {
      ...properties,
      ...(breakpointOverride.style as Partial<CardProperties>),
    };
  }, [component, activeViewport]);

  const { style } = component;

  // CRITICAL FIX: Do NOT spread component.style into cardStyle
  // component.style (padding, margin, background, borderRadius, etc.) belongs ONLY on ComponentWrapper
  // Spreading it here creates duplicate styling where both wrapper and Card have the same
  // styles, causing the wrapper to extend past the Card content.
  // The Card should only use its internal flex properties and responsiveProperties.
  // See ai-editor-system.md Section IV.12 for documentation.

  // Build card styles - ALWAYS explicit background (key difference from FlexContainer)
  const cardStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: responsiveProperties.flexDirection,
    alignItems: responsiveProperties.alignItems,
    justifyContent: responsiveProperties.justifyContent,
    gap:
      typeof responsiveProperties.gap === "number"
        ? `${responsiveProperties.gap}px`
        : responsiveProperties.gap,

    // EXPLICIT background color - never undefined/transparent by default
    // This is the key fix for the background mismatch issue
    backgroundColor: responsiveProperties.backgroundColor,
    color: CONTROL_THEME.cardForeground,

    // Border - use explicit properties
    border:
      responsiveProperties.borderStyle === "none"
        ? "none"
        : `${responsiveProperties.borderWidth}px ${responsiveProperties.borderStyle} ${responsiveProperties.borderColor}`,
    borderRadius: `${responsiveProperties.borderRadius}px`,

    // Shadow
    boxShadow: responsiveProperties.boxShadow,

    // Padding
    padding:
      typeof responsiveProperties.padding === "number"
        ? `${responsiveProperties.padding}px`
        : responsiveProperties.padding,

    // Sizing constraints
    minWidth: responsiveProperties.minWidth,
    maxWidth: responsiveProperties.maxWidth,
    minHeight: responsiveProperties.minHeight,
    maxHeight: responsiveProperties.maxHeight,

    // Standard sizing behavior
    width: "100%",
    height: (() => {
      // Check explicit height sources first (but skip "auto" - we handle that at the end)
      if (
        (typeof style.height === "string" || typeof style.height === "number")
        && style.height !== "auto"
      ) {
        return style.height;
      }
      const explicitPropertyHeight = component.properties.height;
      if (
        (typeof explicitPropertyHeight === "number" || typeof explicitPropertyHeight === "string")
        && explicitPropertyHeight !== "auto"
      ) {
        return explicitPropertyHeight;
      }
      if (typeof component.size?.height === "string" && component.size.height !== "auto") return component.size.height;
      if (typeof component.size?.height === "number") return `${component.size.height}px`;

      // Check if alignSelf: stretch is set anywhere - if so, use 100% to fill parent
      // Check base constraints first (applies to all viewports unless overridden)
      const baseConstraints = component.responsivePosition?.constraints;
      if (baseConstraints?.alignSelf === "stretch") return "100%";

      // Check breakpoint-specific constraints
      const breakpoints = component.responsivePosition?.breakpoints;
      if (breakpoints) {
        // Check mobile breakpoint
        if (breakpoints.mobile?.constraints?.alignSelf === "stretch") return "100%";
        // Check tablet breakpoint
        if (breakpoints.tablet?.constraints?.alignSelf === "stretch") return "100%";
        // Check desktop breakpoint (if exists)
        if (breakpoints.desktop?.constraints?.alignSelf === "stretch") return "100%";
      }

      return "auto";
    })(),
    boxSizing: "border-box",
    overflow: "hidden",
    position: "relative",
  };

  // Check if card is empty
  const isEmpty = !children || children.length === 0;

  // Hover state for empty card in editor mode
  const [isHovered, setIsHovered] = useState(false);

  // Deduplicate children by ID (same pattern as FlexContainer)
  const uniqueChildren = useMemo(() => {
    return Array.from(new Map(children.map((c) => [c.id, c])).values());
  }, [children]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "card-container relative w-full transition-all duration-200"
      )}
      style={{
        ...cardStyle,
        ...(isEmpty && !isPreview && isHovered && {
          borderColor: SEAFOAM_GREEN.DEFAULT,
          backgroundColor: `${SEAFOAM_GREEN.faint}4D`, // 30% opacity
        }),
      }}
      data-component-type="card"
      data-container-id={component.id}
      onMouseEnter={() => isEmpty && !isPreview && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isEmpty ? (
        // Empty state - render empty card (no placeholder visualization)
        <div className="flex-1" />
      ) : (
        // Render children
        <div
          className="w-full"
          style={{
            display: "flex",
            // Only use flex: 1 if NOT centering content - otherwise it defeats vertical centering
            ...(responsiveProperties.justifyContent === "center" ||
               responsiveProperties.justifyContent === "space-around"
              ? { height: "100%" }
              : { flex: 1, minHeight: 0 }),
            flexDirection: responsiveProperties.flexDirection,
            alignItems: responsiveProperties.alignItems,
            justifyContent: responsiveProperties.justifyContent,
            gap:
              typeof responsiveProperties.gap === "number"
                ? `${responsiveProperties.gap}px`
                : responsiveProperties.gap,
          }}
        >
          {uniqueChildren.map((child) => (
            <ComponentWrapper
              key={child.id}
              component={child}
              isFlexChild={true}
              flexDirection={responsiveProperties.flexDirection}
              viewport={activeViewport}
              activeColorScheme={activeColorScheme}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Card;
